import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Github, Loader2, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../services/api';
import { useT } from '../contexts/LocaleContext';

type GitHubStatus = {
  has_token: boolean;
  masked_preview?: string;
  github_username?: string;
  validated_at?: string;
  invalid_since?: string | null;
  last_error?: string | null;
};

const Integrations: React.FC = () => {
  const navigate = useNavigate();
  const t = useT();
  const [status, setStatus] = useState<GitHubStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenInput, setTokenInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const s = await apiService.getGithubIntegration();
      setStatus(s);
      // If nothing configured, show the input by default.
      setShowInput(!s?.has_token);
    } catch {
      setStatus(null);
      setShowInput(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    const token = tokenInput.trim();
    if (!token) {
      toast.error(t('integrations.tokenNeedPaste'));
      return;
    }
    setSaving(true);
    try {
      const resp = await apiService.saveGithubToken(token);
      toast.success(
        t('integrations.tokenSavedAs', { name: resp.github_username || 'user' }),
      );
      setTokenInput('');
      setShowInput(false);
      await reload();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || t('integrations.tokenErrorGeneric');
      toast.error(t('integrations.tokenError', { msg }));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(t('integrations.tokenDeleteConfirm'))) return;
    setDeleting(true);
    try {
      await apiService.deleteGithubToken();
      toast.success(t('integrations.tokenDeleted'));
      await reload();
    } catch (e: any) {
      toast.error(e?.message || t('integrations.tokenDeleteFail'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-6 md:p-8" data-testid="integrations-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        <button
          type="button" onClick={() => navigate('/profile')}
          data-testid="integrations-back"
          className="text-[#A1A1AA] hover:text-white mb-6 -ml-2 inline-flex items-center px-3 py-2 rounded-md hover:bg-white/5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('integrations.back')}
        </button>

        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-2">
          {t('integrations.title')}
        </h1>
        <p className="text-[#A1A1AA] text-base mb-8">
          {t('integrations.subtitle')}
        </p>

        {/* GitHub card */}
        <div
          className="bg-[#0E0F11] border border-white/5 rounded-xl p-6"
          data-testid="integrations-github-card"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-white/5 grid place-items-center shrink-0">
              <Github className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-white font-medium">{t('integrations.github')}</div>
                  <div className="text-xs text-[#A1A1AA] mt-0.5">
                    {t('integrations.githubHint')}
                  </div>
                </div>
                {loading && <Loader2 className="w-4 h-4 text-[#52525B] animate-spin" />}
              </div>

              {status?.has_token && !showInput && (
                <div
                  className={[
                    'mt-4 flex items-center justify-between gap-3 rounded-lg border px-3 py-2',
                    status.invalid_since
                      ? 'border-amber-500/30 bg-amber-950/20 text-amber-200'
                      : 'border-emerald-500/25 bg-emerald-950/20 text-emerald-100',
                  ].join(' ')}
                  data-testid="integrations-github-status"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {status.invalid_since
                      ? <AlertTriangle className="w-4 h-4 shrink-0" />
                      : <ShieldCheck className="w-4 h-4 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">
                        {status.invalid_since
                          ? t('integrations.githubRejected')
                          : t('integrations.githubConnectedAs', { name: status.github_username || 'user' })}
                      </div>
                      <div className="text-[10px] font-mono text-[#A1A1AA] truncate">
                        {status.masked_preview}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button" onClick={() => setShowInput(true)}
                      data-testid="integrations-github-replace"
                      className="px-2.5 py-1 text-[10px] rounded bg-white/10 hover:bg-white/20 text-white"
                    >{t('integrations.replace')}</button>
                    <button
                      type="button" onClick={doDelete} disabled={deleting}
                      data-testid="integrations-github-delete"
                      className="px-2.5 py-1 text-[10px] rounded bg-red-900/40 hover:bg-red-900/60 text-red-200 inline-flex items-center gap-1 disabled:opacity-40"
                    >
                      {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      {t('integrations.delete')}
                    </button>
                  </div>
                </div>
              )}

              {showInput && (
                <div className="mt-4 space-y-2" data-testid="integrations-github-form">
                  <input
                    type="password" autoComplete="off" spellCheck={false}
                    value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    data-testid="integrations-github-input"
                    className="w-full bg-[#050505] border border-white/10 text-white rounded-md px-3 py-2 text-sm placeholder-[#52525B] focus:outline-none focus:border-white/30 font-mono"
                  />
                  <p className="text-[10px] text-[#52525B] leading-snug">
                    {t('integrations.createLink')}{' '}
                    <a
                      href="https://github.com/settings/tokens/new?scopes=repo&description=Nexus+Deploy"
                      target="_blank" rel="noopener noreferrer"
                      className="underline text-[#A1A1AA] hover:text-white"
                      data-testid="integrations-github-pat-link"
                    >
                      github.com/settings/tokens
                    </a>
                    {' '}{t('integrations.scopeHint')}
                  </p>
                  <div className="flex justify-end gap-2 pt-1">
                    {status?.has_token && (
                      <button
                        type="button"
                        onClick={() => { setShowInput(false); setTokenInput(''); }}
                        data-testid="integrations-github-cancel"
                        className="text-xs px-3 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-zinc-100"
                      >
                        {t('integrations.cancel')}
                      </button>
                    )}
                    <button
                      type="button" onClick={save} disabled={saving || !tokenInput.trim()}
                      data-testid="integrations-github-save"
                      className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-100 disabled:opacity-40 inline-flex items-center gap-1.5"
                    >
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                      {saving ? t('integrations.validating') : t('integrations.save')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-[11px] text-[#52525B] mt-6 leading-relaxed">
          {t('integrations.comingSoonNote')}
        </p>
      </motion.div>
    </div>
  );
};

export default Integrations;
